import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Button, Card, ErrorNote, Spinner, StatusBadge } from "@/components/ui";
import { OverflowMenu, type MenuItem } from "@/components/OverflowMenu";
import { WhatsAppSend } from "@/components/WhatsAppSend";
import { DateTimeField, Labeled, Select, TextArea, TextField } from "@/components/Form";
import { tStatus } from "@/i18n";
import { formatCompanyDateTime, zonedWallToIso } from "@/lib/time";
import type { DriverGuide, OperationalTask, Page } from "@/lib/types";

/** The one "next step" button a task row keeps in view, keyed by its status.
 * `unassigned` has none: its next step is picking a driver (see `TaskDriver`). */
const NEXT_STEP: Record<string, { status: string; labelKey: string }> = {
  assigned: { status: "driver_confirmed", labelKey: "operations.confirm" },
  driver_confirmed: { status: "in_progress", labelKey: "operations.start" },
  in_progress: { status: "completed", labelKey: "operations.complete" },
};

/** Everything else lives behind the row's "⋯". Each one asks a second time —
 * handing a trip back tells the driver, cancelling ends the task. */
const MENU_STEPS: Record<string, { status: string; labelKey: string; danger?: boolean }[]> = {
  unassigned: [{ status: "cancelled", labelKey: "operations.cancelTask", danger: true }],
  assigned: [
    { status: "unassigned", labelKey: "operations.handBack" },
    { status: "cancelled", labelKey: "operations.cancelTask", danger: true },
  ],
  driver_confirmed: [
    { status: "unassigned", labelKey: "operations.handBack" },
    { status: "cancelled", labelKey: "operations.cancelTask", danger: true },
  ],
  in_progress: [{ status: "cancelled", labelKey: "operations.cancelTask", danger: true }],
};

const isClosed = (task: OperationalTask) =>
  task.status === "completed" || task.status === "cancelled";

export function useDrivers() {
  return useQuery({
    queryKey: ["drivers-guides", "all"],
    queryFn: () =>
      api.get<Page<DriverGuide>>("/drivers-guides?limit=200"),
  });
}

/** Who has the task, and whether they answered — plus, for whoever may assign,
 * a way to change them at any moment (after they confirmed, mid-trip).
 *
 * With a driver: the name and «подтвердил» / «не ответил» come first, the picker
 * only opens on «Сменить водителя». The empty picker is for a task with nobody
 * on it. A closed task just names its driver — the name comes from the server
 * (`driver_name`), so it is there even for a driver no longer in the active
 * list. */
export function TaskDriver({
  task,
  invalidateKeys,
  canAssign,
}: {
  task: OperationalTask;
  invalidateKeys: unknown[][];
  canAssign: boolean;
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const [changing, setChanging] = useState(false);
  const [driverId, setDriverId] = useState("");
  const act = useEntityAction(invalidateKeys, {
    inlineError: true,
    onSuccess: () => {
      setChanging(false);
      setDriverId("");
    },
  });

  const hasDriver = task.driver_guide_id != null;
  const driver = drivers.data?.items.find((d) => d.id === task.driver_guide_id);
  const name =
    task.driver_name ??
    drivers.data?.items.find((d) => d.id === task.driver_guide_id)?.name ??
    "…";
  const editable = canAssign && !isClosed(task) && !task.deleted_at;
  const showPicker = editable && (!hasDriver || changing);

  // Only while the task is open: once it is closed the status pill says it all.
  const answer = isClosed(task)
    ? null
    : task.driver_confirmed_at || task.status === "driver_confirmed"
      ? { text: t("operations.driverConfirmed"), tone: "bg-emerald-500/15 text-emerald-700" }
      : task.status === "assigned"
        ? { text: t("operations.driverNoReply"), tone: "bg-amber-500/15 text-amber-700" }
        : null;

  const options = (drivers.data?.items ?? []).filter(
    (d) => d.status === "active" && d.id !== task.driver_guide_id,
  );

  return (
    <div className="space-y-1.5" data-testid="task-driver">
      {hasDriver ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-sand">{name}</span>
          {answer && (
            <span className={"rounded-full px-2 py-0.5 text-xs " + answer.tone}>
              {answer.text}
            </span>
          )}
          {editable && !changing && (
            <button
              type="button"
              className="rounded-md border border-olive-600 px-2 py-0.5 text-xs text-sand-muted hover:border-gold hover:text-gold"
              onClick={() => setChanging(true)}
            >
              {t("operations.changeDriver")}
            </button>
          )}
        </div>
      ) : (
        !showPicker && <span className="text-sand-muted">{t("operations.noDriver")}</span>
      )}

      {showPicker && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              className="w-auto min-w-48"
              aria-label={hasDriver ? t("operations.changeDriver") : t("operations.pickDriver")}
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
            >
              <option value="">{t("operations.pickDriver")}</option>
              {options.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} · {tStatus(d.kind)}
                </option>
              ))}
            </Select>
            <Button
              className="px-3 py-1 text-xs"
              loading={act.isPending}
              disabled={!driverId}
              onClick={() =>
                act.mutate(() =>
                  api.post(`/operational-tasks/${task.id}/assign`, {
                    driver_guide_id: driverId,
                  }),
                )
              }
            >
              {hasDriver ? t("operations.changeDriver") : t("operations.assign")}
            </Button>
            {changing && (
              <button
                type="button"
                className="text-xs text-sand-muted hover:text-sand"
                disabled={act.isPending}
                onClick={() => {
                  setChanging(false);
                  setDriverId("");
                }}
              >
                {t("common.cancel")}
              </button>
            )}
          </div>
          {hasDriver && (
            <p className="text-xs text-sand-muted">
              {t("operations.changeDriverHint", { name })}
            </p>
          )}
        </>
      )}
      {/* A driver with no Telegram gets the task by hand, through WhatsApp. */}
      {canAssign && hasDriver && !isClosed(task) && !task.deleted_at &&
        driver && driver.telegram_user_id == null && (
          <WhatsAppSend taskId={task.id} sentAt={task.whatsapp_sent_at} invalidate={invalidateKeys} />
        )}
      {act.isError && <ErrorNote message={(act.error as Error).message} />}
    </div>
  );
}

/** A task row's controls: its one next step in view, the rest behind "⋯"
 * (hand back, cancel, and — when the page passes them — delete / restore).
 * Changing the driver is `TaskDriver`'s job, next to the driver's name. */
export function TaskActions({
  task,
  invalidateKeys,
  canWrite,
  onDelete,
  onRestore,
  rowBusy,
}: {
  task: OperationalTask;
  invalidateKeys: unknown[][];
  canWrite: boolean;
  onDelete?: () => void;
  onRestore?: () => void;
  rowBusy?: boolean;
}) {
  const { t } = useTranslation();
  const act = useEntityAction(invalidateKeys, { inlineError: true });
  if (!canWrite) return null;

  if (task.deleted_at) {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs text-red-700">
          {t("common.deletedBadge")}
        </span>
        {onRestore && (
          <button
            type="button"
            className="rounded-md border border-olive-600 px-2.5 py-1 text-xs font-medium text-sand-muted hover:text-sand disabled:opacity-50"
            disabled={rowBusy}
            onClick={onRestore}
          >
            {t("common.restore")}
          </button>
        )}
      </div>
    );
  }

  const transition = (status: string) =>
    act.mutate(() => api.post(`/operational-tasks/${task.id}/transition`, { status }));

  const next = NEXT_STEP[task.status];
  const items: MenuItem[] = [
    ...(MENU_STEPS[task.status] ?? []).map((s) => ({
      key: s.status,
      label: t(s.labelKey),
      danger: s.danger,
      confirm: true,
      // Cancelling the task of a booking line cancels the line itself: it stops
      // counting in «Ожидается» (it can be returned with «Вернуть в работу»).
      warning:
        s.status === "cancelled" && task.booking_line_id
          ? t("operations.cancelLineWarning")
          : undefined,
      onSelect: () => transition(s.status),
    })),
    ...(onDelete
      ? [
          {
            key: "delete",
            label: t("common.delete"),
            danger: true,
            confirm: true,
            onSelect: onDelete,
          },
        ]
      : []),
  ];

  return (
    <div className="flex items-center gap-2">
      {next && (
        <Button
          className="whitespace-nowrap px-3 py-1 text-xs"
          loading={act.isPending}
          onClick={() => transition(next.status)}
        >
          {t(next.labelKey)}
        </Button>
      )}
      <OverflowMenu items={items} busy={act.isPending || rowBusy} />
      {act.isError && (
        <span className="text-xs text-red-700">{(act.error as Error).message}</span>
      )}
    </div>
  );
}

/** The per-booking panel for *manually created* tasks (list + create + manage).
 *
 * A task that has a `booking_line_id` is the dispatch projection of a booking
 * line — same service, same driver, same pickup time, all kept in sync from the
 * line card — so listing it here would just repeat the line. Only standalone
 * tasks (no line: added by hand, or from an older / package booking) show up.
 * With none, no section is drawn; a small "+ New task" button remains so one can
 * still be added. */
export function BookingOperations({
  bookingId,
  bookingRef,
  canWrite,
  canAssign,
}: {
  bookingId: string;
  bookingRef: string;
  canWrite: boolean;
  canAssign: boolean;
}) {
  const { t } = useTranslation();
  const [showNew, setShowNew] = useState(false);
  const invalidate: unknown[][] = [["op-tasks", bookingId], ["calendar"]];
  const tasks = useQuery({
    queryKey: ["op-tasks", bookingId],
    queryFn: () =>
      api.get<Page<OperationalTask>>(
        `/operational-tasks?booking_id=${bookingId}&limit=100`,
      ),
  });

  if (tasks.isLoading) return <Spinner />;
  if (tasks.isError) return <ErrorNote message={(tasks.error as Error).message} />;
  const manual = tasks.data!.items.filter((task) => task.booking_line_id == null);
  if (manual.length === 0 && !showNew && !canWrite) return null;

  const newTaskButton = canWrite && !showNew && (
    <Button className="px-3 py-1 text-xs" onClick={() => setShowNew(true)}>
      + {t("operations.newTask")}
    </Button>
  );

  return (
    <div>
      {manual.length > 0 ? (
        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-sm font-semibold text-sand">
            {t("operations.tasks")}
          </h2>
          <span className="ml-auto">{newTaskButton}</span>
        </div>
      ) : (
        newTaskButton && <div className="mb-2">{newTaskButton}</div>
      )}

      {showNew && (
        <NewTaskForm
          bookingRef={bookingRef}
          invalidateKeys={invalidate}
          onDone={() => setShowNew(false)}
        />
      )}

      {manual.length > 0 && (
        <div className="space-y-3">
          {manual.map((task) => (
            <Card key={task.id}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium">{task.service ?? t("operations.tasks")}</span>
                <StatusBadge status={task.status} />
                {task.pickup_time && (
                  <span className="text-sand-muted">
                    {t("fields.pickup")}:{" "}
                    {formatCompanyDateTime(task.pickup_time)}
                  </span>
                )}
                {task.hotel && (
                  <span className="text-sand-muted">
                    {t("fields.hotel")}: {task.hotel}
                    {task.room_number ? ` · ${task.room_number}` : ""}
                  </span>
                )}
                {task.baggage && (
                  <span className="text-sand-muted">
                    {t("fields.baggage")}: {task.baggage}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 text-sm">
                  <span className="mr-2 text-xs text-sand-muted">
                    {t("fields.driver")}
                  </span>
                  <TaskDriver task={task} invalidateKeys={invalidate} canAssign={canAssign} />
                </div>
                <TaskActions task={task} invalidateKeys={invalidate} canWrite={canWrite} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTaskForm({
  bookingRef,
  invalidateKeys,
  onDone,
}: {
  bookingRef: string;
  invalidateKeys: unknown[][];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useEntityAction(invalidateKeys, { inlineError: true, onSuccess: onDone });
  const [f, setF] = useState({
    service: "",
    pickup_time: "",
    baggage: "",
    hotel: "",
    room_number: "",
    guest_count: "",
    operational_notes: "",
  });

  return (
    <Card>
      <div className="mb-2 text-sm font-semibold">{t("operations.newTask")}</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Labeled label={t("fields.service")}>
          <TextField
            value={f.service}
            onChange={(e) => setF({ ...f, service: e.target.value })}
          />
        </Labeled>
        <div className="sm:col-span-2">
          <Labeled label={t("fields.pickupDubai")}>
            <DateTimeField
              value={f.pickup_time}
              onChange={(v) => setF({ ...f, pickup_time: v })}
            />
          </Labeled>
        </div>
        <Labeled label={t("fields.baggage")}>
          <TextField
            value={f.baggage}
            onChange={(e) => setF({ ...f, baggage: e.target.value })}
            placeholder={t("operations.baggagePlaceholder")}
          />
        </Labeled>
        <Labeled label={t("fields.hotel")}>
          <TextField
            value={f.hotel}
            onChange={(e) => setF({ ...f, hotel: e.target.value })}
          />
        </Labeled>
        <Labeled label={t("fields.room")}>
          <TextField
            value={f.room_number}
            onChange={(e) => setF({ ...f, room_number: e.target.value })}
          />
        </Labeled>
        <Labeled label={t("fields.guests")}>
          <TextField
            type="number"
            min="0"
            value={f.guest_count}
            onChange={(e) => setF({ ...f, guest_count: e.target.value })}
          />
        </Labeled>
      </div>
      <div className="mt-3">
        <Labeled label={t("fields.notes")}>
          <TextArea
            value={f.operational_notes}
            onChange={(e) => setF({ ...f, operational_notes: e.target.value })}
          />
        </Labeled>
      </div>
      {create.isError && (
        <div className="mt-2">
          <ErrorNote message={(create.error as Error).message} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          loading={create.isPending}
          onClick={() =>
            create.mutate(() =>
              api.post("/operational-tasks", {
                booking_ref: bookingRef,
                service: f.service || null,
                // typed as Dubai wall-clock time, whatever the browser's zone
                pickup_time: f.pickup_time ? zonedWallToIso(f.pickup_time) : null,
                baggage: f.baggage || null,
                hotel: f.hotel || null,
                room_number: f.room_number || null,
                guest_count: f.guest_count ? Number(f.guest_count) : null,
                operational_notes: f.operational_notes || null,
              }),
            )
          }
        >
          {t("common.create")}
        </Button>
        <button
          className="text-sm text-sand-muted hover:text-sand"
          onClick={onDone}
        >
          {t("common.cancel")}
        </button>
      </div>
    </Card>
  );
}
